import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { AgGridReact } from '@ag-grid-community/react';
import { Grid } from '@mui/material';
import { useAgGrid } from '@tassat/ag-grid';
import { AdminRoleId, AgGridColumnDef } from '@tassat/common-definitions';
import { FirmRecord, UserRecord } from '@tassat/fred-definitions';
import { UserAccountMapped } from '@tassat/fred-extended-definitions';
import { isEqual } from 'lodash';
import React from 'react';

import 'SharedComponents/agGrid/styles/agGridOverrides.css';
import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import { useAppSelector } from 'Reducers/store/hooks';
import { getUserAdminRoleId } from 'Selectors/common';
import { getMappedUserAccountsByUserIdAdmin } from 'Selectors/userAccounts';
import ActionButtonCellRenderer from 'SharedComponents/cellRenderComponent/actionButtonsCellRenderer';
import BooleanCellRenderer from 'SharedComponents/cellRenderComponent/booleanCellRenderer';
import UserAccountRoleCellRenderer from 'SharedComponents/cellRenderComponent/userAccountRoleCellRenderer';
import { IFeaturesContext, useFeatures } from 'SharedComponents/hooks/useFeatures';

interface UserAccountsGridProps {
    firm: FirmRecord;
    user: UserRecord;
}

const editableColumnDefs: (firm: Pick<FirmRecord, 'FirmId'>, features: IFeaturesContext, userAccounts: UserAccountMapped[]) => AgGridColumnDef[] = (firm: Pick<FirmRecord, 'FirmId'>, features: IFeaturesContext, userAccounts: UserAccountMapped[]) => {
    const defaultColumnDefs: AgGridColumnDef[] = [
        {
            field: 'account.AccountName',
            headerName: 'Account Name',
            menuTabs: ['filterMenuTab'],
            flex: 11
        },
        {
            field: 'account.AccessControl',
            headerName: 'Access Control',
            maxWidth: 175,
            cellRenderer: 'booleanRenderer',
            menuTabs: ['filterMenuTab'],
            flex: 5
        },
        {
            field: 'userAccount.ClientRoleId',
            headerName: 'Role',
            cellRenderer: 'userAccountRoleRenderer',
            cellRendererParams: { firmId: firm?.FirmId },
            menuTabs: [],
            valueGetter: ({ data }) => ({
                IsSCEnabled: data.userAccount.IsSCEnabled,
                ClientRoleId: data.userAccount.ClientRoleId
            }),
            valueFormatter: () => '',
            flex: 8
        },
        {
            field: 'userAccount',
            headerName: 'Actions',
            headerClass: 'rightAligned',
            cellClass: 'rightAlignedCell',
            cellRenderer: 'actionButtonsCellRenderer',
            cellRendererParams: {
                actionButtonOptions: {
                    hasDelete: false,
                    hasLock: false
                }
            },
            valueFormatter: () => '',
            width: 100,
            menuTabs: []
        }
    ];

    return defaultColumnDefs;
};

const readonlyColumnDefs: (firm: Pick<FirmRecord, 'FirmId'>, features: IFeaturesContext, userAccounts: UserAccountMapped[]) => AgGridColumnDef[] = (firm: Pick<FirmRecord, 'FirmId'>, features: IFeaturesContext, userAccounts: UserAccountMapped[]) => {
    const defaultColumnDefs: AgGridColumnDef[] = [
        {
            field: 'account.AccountName',
            headerName: 'Account Name',
            menuTabs: ['filterMenuTab'],
            flex: 11
        },
        {
            field: 'account.AccessControl',
            headerName: 'Access Control',
            maxWidth: 175,
            cellRenderer: 'booleanRenderer',
            menuTabs: ['filterMenuTab'],
            flex: 5
        },
        {
            field: 'userAccount.ClientRoleId',
            headerName: 'Role',
            cellRenderer: 'userAccountRoleRenderer',
            cellRendererParams: { firmId: firm?.FirmId },
            menuTabs: ['filterMenuTab'],
            valueGetter: ({ data }) => ({
                IsSCEnabled: data.userAccount.IsSCEnabled,
                ClientRoleId: data.userAccount.ClientRoleId
            }),
            valueFormatter: () => '',
            flex: 8
        }
    ];

    return defaultColumnDefs;
};

export function UserAccountsGrid (props: UserAccountsGridProps) {
    const { firm } = props;

    const userAccounts = useAppSelector((state) => getMappedUserAccountsByUserIdAdmin(state, props.user.UserId, '', true, false));
    const userRole = useAppSelector(getUserAdminRoleId);
    const [features] = useFeatures();
    const [ rowData, setRowData ] = React.useState(userAccounts);
    const [ , onGridReady ] = useAgGrid();

    React.useEffect(() => {
        if (rowData && !isEqual(rowData, userAccounts)) {
            setRowData(userAccounts);
        }
    }, [userAccounts]);

    const columnDefs: AgGridColumnDef[] = React.useMemo(() => {
        const editable = userRole !== AdminRoleId.BANK_OPS_SUPERVISOR && userRole !== AdminRoleId.PCG_ADMIN && !firm?.IsClosed;

        return editable ?
            editableColumnDefs({ FirmId: firm?.FirmId }, features, userAccounts)
            : readonlyColumnDefs({ FirmId: firm?.FirmId }, features, userAccounts);
    }, [
        firm?.FirmId,
        firm?.IsClosed,
        userAccounts,
        userRole,
        features
    ]);

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <div
                className='ag-theme-material'
                id='userAccountsGrid'
                style={{ height: '420px' }}
            >
                <AgGridReact
                    animateRows={false}
                    columnDefs={columnDefs}
                    columnMenu='legacy'
                    components={{
                        booleanRenderer: BooleanCellRenderer,
                        actionButtonsCellRenderer: ActionButtonCellRenderer,
                        userAccountRoleRenderer: UserAccountRoleCellRenderer
                    }}
                    context={{ features }}
                    defaultColDef={{
                        resizable: true,
                        filter: true,
                        sortable: true
                    }}
                    enableCellTextSelection={true}
                    getRowId={({ data }) => data.userAccount.UserAccountId}
                    headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                    onGridReady={onGridReady}
                    rowData={rowData}
                    suppressCellFocus={true}
                    suppressContextMenu={true}
                />
            </div>
        </Grid>
    );
}

export default UserAccountsGrid;